import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { layoutBoxLabel, BoxLabelPreview } from './boxLabelLayout';
import { useBoxLabelPrinter, boxLabelLink } from './useBoxLabelPrinter';
import LabelPreviewFrame, { useLabelSize, previewScale } from './LabelPreviewFrame';

// The label preview (issues #300, #305, #313, #445): one small label per
// container, a Parcel label while it's outermost and a Package label once
// it's inside another, printed straight to the packing station's Zebra
// printer for the chosen size (useBoxLabelPrinter). See boxLabelLayout.js
// for what's on it.
//
// The code is a web link (<this site>/b/<label token>), so any phone camera
// opens it; see BoxScanPage.js for what it shows. Pick Order's scan field
// also takes it.
//
// Opens at the tenant's default Label Size (Warehouse Settings); staff can
// choose another size and print. The preview shows the label as it will
// print. Closing without printing is fine — the package packs, ships and
// goes into another the same either way.
function BoxLabelView({ packageId, onClose }) {
  const [label, setLabel] = useState(null);
  const [error, setError] = useState('');
  const [printing, setPrinting] = useState(false);
  const [printed, setPrinted] = useState('');
  const sizing = useLabelSize(label?.default_size);
  const { size, dpi } = sizing;
  const { printLabel, problemEl, chooserEl } = useBoxLabelPrinter();

  useEffect(() => {
    setLabel(null);
    setError('');
    axios.get(`/api/fulfilment/package-label/${packageId}`)
      .then((res) => setLabel(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Could not load the Package Label'));
  }, [packageId]);

  const layout = useMemo(
    () => (label && size ? layoutBoxLabel({ label, link: boxLabelLink(label.label_token), size, dpi }) : null),
    [label, size, dpi],
  );

  const handlePrint = async () => {
    setPrinting(true);
    setPrinted('');
    try {
      const result = await printLabel(label, size);
      if (result) setPrinted(`${label.package_name} label printed on ${result.printer.name}`);
    } catch {
      // problemEl says what went wrong.
    }
    setPrinting(false);
  };

  return (
    <LabelPreviewFrame
      title="Package Label"
      subtitle={label ? `${label.display_number} · ${label.box_number}${label.package_name !== label.box_number ? ` (${label.package_name})` : ''}` : null}
      sizing={sizing}
      onSize={(id) => { sizing.setSizeId(id); setPrinted(''); }}
      onPrint={handlePrint}
      canPrint={!!label && !!size && !printing}
      printing={printing}
      onClose={onClose}
    >
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {!label && !error && <div>Loading...</div>}
      {label && !size && <div style={{ color: '#b91c1c' }}>No Label Sizes are set up. Add one on Warehouse › Label Sizes.</div>}
      {problemEl && <div style={{ marginBottom: 12 }}>{problemEl}</div>}
      {printed && <div style={{ marginBottom: 12, color: '#1e7e34', fontWeight: 600, fontSize: 13 }}>{printed}</div>}
      {layout?.overflow && (
        <div style={{ marginBottom: 12, fontSize: 13, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 8, padding: '8px 12px' }}>
          This package&apos;s contents don&apos;t fit on a {size.name} label, so it lists how many SKUs the package holds and leaves the rest to the code. Print it as is, or choose a bigger size.
        </div>
      )}
      {layout && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 16, background: '#eceef1', borderRadius: 6, overflowX: 'auto' }}>
          <div style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.25)' }}>
            <BoxLabelPreview layout={layout} scale={previewScale(size)} />
          </div>
        </div>
      )}
      {chooserEl}
    </LabelPreviewFrame>
  );
}

export default BoxLabelView;
